import { useState, useMemo, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  manpowerApi,
  lookupsApi,
  type MpLeader,
  type MpLeaderInput,
  type LookupOption,
} from '@/lib/apiClient';
import { useFocusTrap } from '@/lib/hooks/useFocusTrap';
import { fmtDate, toDateInputValue, daysFromToday } from '@/lib/dates';

// ─── Shared style constants (matched to ManpowerPage) ────────────────────────

const TH: React.CSSProperties = {
  background: 'var(--ew-dark)',
  color: 'var(--ew-white)',
  fontWeight: 600,
  fontSize: 11,
  padding: '8px 10px',
  textAlign: 'left',
  whiteSpace: 'nowrap',
  borderRight: '1px solid rgba(255,255,255,0.07)',
  cursor: 'pointer',
  userSelect: 'none',
};

const TD: React.CSSProperties = {
  borderBottom: '1px solid var(--ew-n-100)',
  borderRight: '1px solid var(--ew-n-100)',
  padding: '7px 10px',
  fontSize: 12,
  verticalAlign: 'middle',
};

// ─── Inline cell editing ─────────────────────────────────────────────────────

/**
 * The four columns that can be edited from the row (issue #349). Clicking one
 * opens the edit modal with that field focused, rather than editing the cell in
 * place.
 *
 * WHY A MODAL AND NOT AN INLINE CELL EDITOR. These four fields are one story
 * about a leader's timeline — current job, when it ends, next job, when they're
 * free — and the row badges ("Ends in 8d", "8d gap", "No next assignment") are
 * derived from COMBINATIONS of them. Saving them one at a time walks the row
 * through states that are individually valid but jointly nonsense, and flashes
 * badges that are simply wrong. The modal already submits all four in a single
 * PATCH, so editing a leader stays atomic without needing a draft/publish
 * layer like the crew schedule's (mp_schedule_drafts). That layer exists there
 * because PMs read the published board; nothing outside this tab reads these
 * columns, so there is no audience to stage edits away from.
 */
type FocusField =
  | 'currentAssignmentId'
  | 'currentFinish'
  | 'upcomingJobId'
  | 'nextAvailable';

/** What the edit modal is open for: an existing leader, or a new one. */
type EditTarget = { leader: MpLeader | null; focus?: FocusField } | null;

/**
 * Hover/focus affordance for an editable cell. Kept as a module-local class
 * because inline styles can't express :hover — see the pw-card/qq-table
 * convention. The name is scoped to this file and used nowhere else.
 */
const INLINE_EDIT_CSS = `
.lv-cell-edit {
  display: block;
  width: 100%;
  min-height: 20px;
  text-align: left;
  background: none;
  border: 1px dashed transparent;
  border-radius: var(--ew-r-sm);
  padding: 1px 4px;
  margin: -1px -4px;
  font: inherit;
  color: inherit;
  cursor: pointer;
}
.lv-cell-edit:hover { border-color: var(--ew-n-300); background: rgba(0,0,0,0.02); }
.lv-cell-edit:focus-visible { outline: 2px solid var(--ew-green-700); outline-offset: 1px; }
`;

// ─── Assignment-health status (drives conditional row formatting) ────────────

/**
 * A field leader is "approaching end of assignment" when their current job's
 * finish date falls within this window. Two weeks is the smallest realistic
 * mobilization gap, so it's the threshold at which a PM should be actively
 * lining up the next assignment.
 */
const ENDING_SOON_DAYS = 14;

/** Gap (in days) between currentFinish and nextAvailable that warrants a badge. */
const GAP_THRESHOLD_DAYS = 7;

type LeaderSeverity = 'overdue' | 'critical' | 'warning';

interface LeaderStatus {
  /** Worst severity for the row — controls the background tint. */
  severity: LeaderSeverity | null;
  /** Whole days from today (local midnight) to currentFinish. Negative if past. */
  daysToFinish: number | null;
  /** True if currentFinish is already in the past. */
  overdue: boolean;
  /** True if currentFinish is today..+ENDING_SOON_DAYS. */
  endingSoon: boolean;
  /** True if ending-soon and no upcomingJob — the "especially" case from the request. */
  noNext: boolean;
  /** Whole days between currentFinish and nextAvailable. Null if either is missing. */
  gapDays: number | null;
}

function getLeaderStatus(l: MpLeader): LeaderStatus {
  const hasCurrent = !!l.currentAssignment;
  const daysToFinish = hasCurrent ? daysFromToday(l.currentFinish) : null;
  const hasNext = !!l.upcomingJob;

  const overdue =
    daysToFinish !== null && daysToFinish < 0;
  const endingSoon =
    daysToFinish !== null && daysToFinish >= 0 && daysToFinish <= ENDING_SOON_DAYS;
  const noNext = endingSoon && !hasNext;

  const daysToNextAvail = daysFromToday(l.nextAvailable);
  const gapDays =
    hasNext && daysToFinish !== null && daysToNextAvail !== null
      ? daysToNextAvail - daysToFinish
      : null;

  let severity: LeaderSeverity | null = null;
  if (overdue) severity = 'overdue';
  else if (noNext) severity = 'critical';
  else if (endingSoon) severity = 'warning';

  return { severity, daysToFinish, overdue, endingSoon, noNext, gapDays };
}

/** Background tints per severity — kept low-alpha so the table stays scannable. */
const ROW_BG: Record<LeaderSeverity, string> = {
  overdue:  'rgba(184, 50, 28, 0.06)',   // var(--ew-danger) at 6%
  critical: 'rgba(184, 50, 28, 0.06)',   // same red — visually one "this needs attention" bucket
  warning:  'rgba(219, 122, 37, 0.08)',  // var(--ew-performance) at 8%
};

/**
 * Urgency tier used as the *primary* sort key so attention-needing leaders
 * surface at the top regardless of which column the user has clicked. Lower
 * rank = higher in the table.
 */
const SEVERITY_RANK: Record<LeaderSeverity | 'none', number> = {
  overdue:  0,
  critical: 1,
  warning:  2,
  none:     3,
};

function endingSoonLabel(days: number): string {
  if (days === 0) return 'Ends today';
  if (days === 1) return 'Ends tomorrow';
  return `Ends in ${days}d`;
}

interface ProjectLite {
  id: number;
  jobNumber: string;
  title: string;
  isArchived?: boolean;
}

// ─── Sorting ─────────────────────────────────────────────────────────────────

type SortKey =
  | 'name' | 'division' | 'class'
  | 'currentAssignment' | 'currentFinish'
  | 'upcomingJob' | 'nextAvailable' | 'travel';

interface SortState {
  key: SortKey;
  dir: 'asc' | 'desc';
}

function sortValue(l: MpLeader, key: SortKey): string {
  switch (key) {
    case 'name':              return l.name?.toLowerCase() ?? '';
    case 'division':          return l.division?.label.toLowerCase() ?? '';
    case 'class':             return (l.class ?? '').toLowerCase();
    case 'currentAssignment': return l.currentAssignment?.title.toLowerCase() ?? '';
    case 'currentFinish':     return l.currentFinish ?? '';
    case 'upcomingJob':       return l.upcomingJob?.title.toLowerCase() ?? '';
    case 'nextAvailable':     return l.nextAvailable ?? '';
    // Sorted by tier deliberately, not alphabetically: 'Traveler' before
    // '50/50' before 'Local' is the order a scheduler is looking for, and it
    // is not the order the words fall in. Unlinked rows sort last.
    case 'travel':            return TRAVEL_SORT[l.travel?.travelPref ?? ''] ?? '9';
  }
}

/** Tier order for sorting. See sortValue('travel'). */
const TRAVEL_SORT: Record<string, string> = { 'Traveler': '1', '50/50': '2', 'Local': '3' };

/**
 * What the Travel cell says, and why blank is not good enough.
 *
 * ⚠️ THREE STATES LOOK IDENTICAL IF YOU JUST RENDER `travel?.travelPref`, and
 * a scheduler reading a blank cell will assume the third:
 *
 *   1. No employee id      — nobody ever matched this leader to Paycor.
 *   2. Id, but no roster row — matched, but not on the manpower lead's travel list.
 *   3. A real preference.
 *
 * States 1 and 2 are missing DATA, not a person who will not travel. Saying
 * "Local" or "—" for either is the failure that sends the wrong crew.
 *
 * Exported so the test pins the distinction rather than the markup.
 */
export function travelCellState(l: Pick<MpLeader, 'employeeId' | 'travel'>):
  { kind: 'unlinked' | 'no-roster' | 'known'; label: string; title: string } {
  if (l.travel) {
    return { kind: 'known', label: l.travel.travelPref, title: 'From the travel roster' };
  }
  if (!l.employeeId) {
    return {
      kind: 'unlinked',
      label: 'Not linked',
      title: 'This leader has no Paycor employee id, so their travel preference cannot be looked up. Add one in Edit.',
    };
  }
  return {
    kind: 'no-roster',
    label: 'Not on roster',
    title: 'Linked to Paycor, but this person is not on the travel roster. Add them on the Travel tab.',
  };
}

// ─── Main component ──────────────────────────────────────────────────────────

interface LeadersViewProps {
  canEdit: boolean;
}

export default function LeadersView({ canEdit }: LeadersViewProps) {
  const queryClient = useQueryClient();

  const [divisionFilter, setDivisionFilter] = useState<number | null>(null);
  const [sort, setSort] = useState<SortState>({ key: 'name', dir: 'asc' });
  const [editTarget, setEditTarget] = useState<EditTarget>(null);

  // ── Queries ──────────────────────────────────────────────────────────────
  const { data: leaders = [], isLoading, isError } = useQuery<MpLeader[]>({
    queryKey: ['manpower-leaders'],
    queryFn: () => manpowerApi.leaders(),
    staleTime: 30_000,
  });

  const { data: divisions = [] } = useQuery<LookupOption[]>({
    queryKey: ['lookups-divisions'],
    queryFn: lookupsApi.listDivisions,
    staleTime: 5 * 60 * 1000,
  });

  // Projects available to assign to a field leader. Excludes archived (server
  // filter) plus Complete/Canceled (client filter on projectStatus.label) so
  // the picker stays focused on jobs that could still need a leader.
  const { data: projectsRaw = [] } = useQuery<ProjectLite[]>({
    queryKey: ['manpower-project-options', { includeArchived: false }],
    queryFn: () => manpowerApi.projectOptions(false),
    staleTime: 60_000,
    select: (d: any[]) =>
      d.filter((p: any) => {
        const status = p.projectStatus?.label;
        return status !== 'Complete' && status !== 'Canceled';
      })
       .map((p: any) => ({ id: p.id, jobNumber: p.jobNumber, title: p.title })),
  });

  // ── Derived ──────────────────────────────────────────────────────────────
  // Job-number order for both the inline cell dropdowns and the edit modal.
  const projectOptions = useMemo(
    () => [...projectsRaw].sort((a, b) => a.jobNumber.localeCompare(b.jobNumber)),
    [projectsRaw],
  );

  const filtered = useMemo(() => {
    if (divisionFilter === null) return leaders;
    return leaders.filter((l) => l.divisionId === divisionFilter);
  }, [leaders, divisionFilter]);

  const sorted = useMemo(() => {
    const out = [...filtered];
    out.sort((a, b) => {
      // Urgency tier is the primary key — overdue → critical → warning → normal
      // — so anyone needing attention surfaces at the top regardless of which
      // column the user has chosen to sort by. Within a tier we fall through
      // to the column sort below.
      const ra = SEVERITY_RANK[getLeaderStatus(a).severity ?? 'none'];
      const rb = SEVERITY_RANK[getLeaderStatus(b).severity ?? 'none'];
      if (ra !== rb) return ra - rb;

      const av = sortValue(a, sort.key);
      const bv = sortValue(b, sort.key);
      // Empty values always sort to the bottom regardless of direction,
      // so users don't have to scroll past blanks to find real assignments.
      if (av === '' && bv !== '') return 1;
      if (bv === '' && av !== '') return -1;
      if (av < bv) return sort.dir === 'asc' ? -1 : 1;
      if (av > bv) return sort.dir === 'asc' ? 1 : -1;
      return 0;
    });
    return out;
  }, [filtered, sort]);

  function toggleSort(key: SortKey) {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' },
    );
  }

  function sortIndicator(key: SortKey): string {
    if (sort.key !== key) return '';
    return sort.dir === 'asc' ? ' ▲' : ' ▼';
  }

  // ── Mutations ────────────────────────────────────────────────────────────
  const deactivateMutation = useMutation({
    mutationFn: (id: number) => manpowerApi.deactivateLeader(id),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['manpower-leaders'] }),
  });

  /** Open the edit modal for this leader, focused on the clicked column. */
  function openEditor(leader: MpLeader, focus: FocusField) {
    setEditTarget({ leader, focus });
  }

  function handleRemove(leader: MpLeader) {
    if (!canEdit) return;
    const ok = window.confirm(
      `Remove ${leader.name} from the field-leader list?\n\n` +
      `They'll be deactivated but their history (assignments, schedule entries) is preserved.`,
    );
    if (ok) deactivateMutation.mutate(leader.id);
  }

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div>
      <style>{INLINE_EDIT_CSS}</style>

      {/* Toolbar — division filter + Add button */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ew-n-500)' }}>
          Division:
        </span>
        <button
          type="button"
          onClick={() => setDivisionFilter(null)}
          style={chipStyle(divisionFilter === null)}
        >
          All <span style={{ fontSize: 10, fontWeight: 500, opacity: 0.75 }}>({leaders.length})</span>
        </button>
        {divisions.map((d) => {
          const count = leaders.filter((l) => l.divisionId === d.id).length;
          return (
            <button
              key={d.id}
              type="button"
              onClick={() => setDivisionFilter(d.id)}
              style={chipStyle(divisionFilter === d.id)}
            >
              {d.label} <span style={{ fontSize: 10, fontWeight: 500, opacity: 0.75 }}>({count})</span>
            </button>
          );
        })}

        {canEdit && (
          <button
            className="btn btn-primary"
            style={{ marginLeft: 'auto', fontSize: 12, padding: '6px 12px' }}
            onClick={() => setEditTarget({ leader: null })}
          >
            + Add Field Leader
          </button>
        )}
      </div>

      {!canEdit && (
        <div style={{ marginBottom: 10, fontSize: 12, color: 'var(--ew-n-500)' }}>
          You are in read-only mode. Edit controls are limited to Admin and ManpowerEditor roles.
        </div>
      )}

      {canEdit && (
        <div style={{ marginBottom: 10, fontSize: 12, color: 'var(--ew-n-500)' }}>
          Tip: click any assignment or date cell to edit that leader — all four fields save together.
        </div>
      )}

      {/* Leaders table */}
      <div style={{ overflowX: 'auto', borderRadius: 'var(--ew-r-lg)', border: '1px solid var(--ew-n-200)', boxShadow: 'var(--ew-shadow-2)' }}>
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 64, color: 'var(--ew-n-400)', fontSize: 14 }}>Loading field leaders…</div>
        ) : isError ? (
          <div style={{ textAlign: 'center', padding: 64, color: 'var(--ew-danger)', fontSize: 14 }}>Failed to load field leaders.</div>
        ) : sorted.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 64, color: 'var(--ew-n-400)', fontSize: 14 }}>
            {leaders.length === 0
              ? 'No field leaders yet. Click "Add Field Leader" to create one.'
              : 'No field leaders match this division filter.'}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--ew-font)' }}>
            <thead>
              <tr>
                <th style={TH} onClick={() => toggleSort('name')}>Name{sortIndicator('name')}</th>
                <th style={TH} onClick={() => toggleSort('division')}>Division{sortIndicator('division')}</th>
                <th style={TH} onClick={() => toggleSort('class')}>Class{sortIndicator('class')}</th>
                <th style={TH} onClick={() => toggleSort('currentAssignment')}>Current Assignment{sortIndicator('currentAssignment')}</th>
                <th style={TH} onClick={() => toggleSort('currentFinish')}>Finish Date{sortIndicator('currentFinish')}</th>
                <th style={TH} onClick={() => toggleSort('upcomingJob')}>Next Assignment{sortIndicator('upcomingJob')}</th>
                <th style={TH} onClick={() => toggleSort('nextAvailable')}>Next Available{sortIndicator('nextAvailable')}</th>
                {/* Read-only, from mp_travel_roster (#396). Editing lives on the Travel tab. */}
                <th style={TH} onClick={() => toggleSort('travel')}>Travel{sortIndicator('travel')}</th>
                {canEdit && <th style={{ ...TH, cursor: 'default', textAlign: 'center', width: 110 }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {sorted.map((l) => {
                const status = getLeaderStatus(l);
                const rowBg = status.severity ? ROW_BG[status.severity] : 'var(--ew-white)';
                return (
                <tr key={l.id} style={{ background: rowBg }}>
                  <td style={{ ...TD, fontWeight: 600 }}>{l.name}</td>
                  <td style={TD}>{l.division?.label ?? <span style={{ color: 'var(--ew-n-400)' }}>—</span>}</td>
                  <td style={TD}>{l.class ?? <span style={{ color: 'var(--ew-n-400)' }}>—</span>}</td>
                  <td style={TD}>
                    <EditableCell
                      canEdit={canEdit}
                      label="current assignment"
                      onOpen={() => openEditor(l, 'currentAssignmentId')}
                    >
                      {l.currentAssignment ? (
                          <>
                            <span style={{ color: 'var(--ew-n-500)', fontVariantNumeric: 'tabular-nums', marginRight: 6 }}>
                              {l.currentAssignment.jobNumber}
                            </span>
                            {l.currentAssignment.title}
                            {l.assignmentShift && (
                              <span
                                title={`${l.assignmentShift} shift — same job, different shift`}
                                style={{ marginLeft: 6, display: 'inline-block', padding: '1px 7px', fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', background: '#312e81', color: '#e0e7ff', borderRadius: 'var(--ew-r-pill)', verticalAlign: 'middle' }}
                              >
                                🌙 {l.assignmentShift}
                              </span>
                            )}
                          </>
                      ) : (
                        <span style={{ color: 'var(--ew-n-400)' }}>Unassigned</span>
                      )}
                    </EditableCell>
                  </td>
                  <td style={{ ...TD, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    <EditableCell
                      canEdit={canEdit}
                      label="finish date"
                      onOpen={() => openEditor(l, 'currentFinish')}
                    >
                      {fmtDate(l.currentFinish)}
                      {status.overdue && <Badge tone="danger">Past finish</Badge>}
                      {!status.overdue && status.endingSoon && (
                        <Badge tone={status.noNext ? 'danger' : 'warning'}>
                          {endingSoonLabel(status.daysToFinish!)}
                        </Badge>
                      )}
                    </EditableCell>
                  </td>
                  <td style={TD}>
                    <EditableCell
                      canEdit={canEdit}
                      label="next assignment"
                      onOpen={() => openEditor(l, 'upcomingJobId')}
                    >
                      {l.upcomingJob ? (
                        <>
                          <span style={{ color: 'var(--ew-n-500)', fontVariantNumeric: 'tabular-nums', marginRight: 6 }}>
                            {l.upcomingJob.jobNumber}
                          </span>
                          {l.upcomingJob.title}
                        </>
                      ) : status.noNext ? (
                        <Badge tone="danger" style={{ marginLeft: 0 }}>No next assignment</Badge>
                      ) : (
                        <span style={{ color: 'var(--ew-n-400)' }}>—</span>
                      )}
                    </EditableCell>
                  </td>
                  <td style={{ ...TD, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    <EditableCell
                      canEdit={canEdit}
                      label="next available date"
                      onOpen={() => openEditor(l, 'nextAvailable')}
                    >
                      {fmtDate(l.nextAvailable)}
                      {status.gapDays !== null && status.gapDays >= GAP_THRESHOLD_DAYS && (
                        <Badge tone="warning">{status.gapDays}d gap</Badge>
                      )}
                    </EditableCell>
                  </td>
                  {/* ── Travel, from the roster (#396) ────────────────────────
                      NOT an EditableCell. mp_travel_roster owns this and the
                      Travel tab edits it; a second editor would be a second
                      copy of the field the supply figures are built on.
                      The two missing-data states are labelled rather than left
                      blank — see travelCellState. */}
                  <td style={{ ...TD, whiteSpace: 'nowrap' }}>
                    <TravelCell leader={l} />
                  </td>
                  {canEdit && (
                    <td style={{ ...TD, textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <button
                        className="btn btn-secondary"
                        style={{ fontSize: 11, padding: '3px 8px', marginRight: 4 }}
                        onClick={() => setEditTarget({ leader: l })}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-secondary"
                        style={{ fontSize: 11, padding: '3px 8px', color: 'var(--ew-danger)' }}
                        onClick={() => handleRemove(l)}
                        disabled={deactivateMutation.isPending}
                      >
                        Remove
                      </button>
                    </td>
                  )}
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {editTarget !== null && (
        <LeaderEditModal
          leader={editTarget.leader}
          focusField={editTarget.focus}
          divisions={divisions}
          projects={projectOptions}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['manpower-leaders'] });
            setEditTarget(null);
          }}
        />
      )}
    </div>
  );
}

// ─── Chip helper ─────────────────────────────────────────────────────────────

function chipStyle(active: boolean): React.CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 10px',
    fontSize: 12,
    fontWeight: 600,
    border: `1px solid ${active ? 'var(--ew-green-100)' : 'var(--ew-n-200)'}`,
    borderRadius: 'var(--ew-r-pill)',
    cursor: 'pointer',
    userSelect: 'none',
    background: active ? 'var(--ew-green-50)' : 'var(--ew-white)',
    color: active ? 'var(--ew-green-700)' : 'var(--ew-n-500)',
    fontFamily: 'var(--ew-font)',
  };
}

// ─── Edit modal ──────────────────────────────────────────────────────────────

interface LeaderEditModalProps {
  leader: MpLeader | null;
  divisions: LookupOption[];
  projects: ProjectLite[];
  /** Which control to focus on open — set when the user clicked a row cell. */
  focusField?: FocusField;
  onClose: () => void;
  onSaved: () => void;
}

function LeaderEditModal({ leader, divisions, projects, focusField, onClose, onSaved }: LeaderEditModalProps) {
  const isNew = leader === null;

  const [name, setName] = useState(leader?.name ?? '');
  const [divisionId, setDivisionId] = useState<number | ''>(leader?.divisionId ?? '');
  const [classText, setClassText] = useState(leader?.class ?? '');
  const [currentAssignmentId, setCurrentAssignmentId] = useState<number | ''>(leader?.currentAssignmentId ?? '');
  const [assignmentShift, setAssignmentShift] = useState(leader?.assignmentShift ?? '');
  const [currentFinish, setCurrentFinish] = useState(toDateInputValue(leader?.currentFinish));
  const [upcomingJobId, setUpcomingJobId] = useState<number | ''>(leader?.upcomingJobId ?? '');
  const [nextAvailable, setNextAvailable] = useState(toDateInputValue(leader?.nextAvailable));
  const [employeeId, setEmployeeId] = useState(leader?.employeeId ?? '');

  const nameRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLFormElement>(null);
  useFocusTrap(modalRef, { onEscape: onClose });

  // Land the caret on the column the user actually clicked, so a row click
  // behaves like editing that cell even though the save is whole-leader.
  const focusRefs: Record<FocusField, React.RefObject<HTMLElement | null>> = {
    currentAssignmentId: useRef<HTMLElement>(null),
    currentFinish:       useRef<HTMLElement>(null),
    upcomingJobId:       useRef<HTMLElement>(null),
    nextAvailable:       useRef<HTMLElement>(null),
  };
  useEffect(() => {
    const el = focusField ? focusRefs[focusField].current : null;
    (el ?? nameRef.current)?.focus();
    // Focus once on open; re-running would steal focus mid-edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusField]);

  const projectOptions = useMemo(
    () => [...projects].sort((a, b) => a.jobNumber.localeCompare(b.jobNumber)),
    [projects],
  );

  const saveMutation = useMutation({
    mutationFn: (payload: MpLeaderInput) =>
      isNew
        ? manpowerApi.createLeader(payload)
        : manpowerApi.updateLeader(leader!.id, payload),
    onSuccess: onSaved,
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    const payload: MpLeaderInput = {
      name: name.trim(),
      divisionId: divisionId === '' ? null : Number(divisionId),
      class: classText.trim() || null,
      currentAssignmentId: currentAssignmentId === '' ? null : Number(currentAssignmentId),
      assignmentShift: assignmentShift || null,
      currentFinish: currentFinish || null,
      upcomingJobId: upcomingJobId === '' ? null : Number(upcomingJobId),
      nextAvailable: nextAvailable || null,
      // '' clears it. The API rejects an id another leader already holds and
      // names them, so a mis-resolved match is caught rather than swallowed.
      employeeId: employeeId.trim() || null,
    };
    saveMutation.mutate(payload);
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <form
        ref={modalRef}
        onSubmit={handleSubmit}
        style={{ background: 'var(--ew-white)', borderRadius: 'var(--ew-r-lg)', width: '100%', maxWidth: 560, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: 'var(--ew-shadow-3)', fontFamily: 'var(--ew-font)' }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '18px 24px', borderBottom: '1px solid var(--ew-n-200)' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
            {isNew ? 'Add Field Leader' : `Edit ${leader!.name}`}
          </h3>
          <button type="button" onClick={onClose} aria-label="Close field leader form" style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--ew-n-400)', lineHeight: 1, padding: '0 4px' }}>×</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 24px', display: 'grid', gap: 14 }}>
          <Field label="Name" required>
            <input
              ref={nameRef}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              required
              style={inputStyle}
            />
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="Division">
              <select
                value={divisionId}
                onChange={(e) => setDivisionId(e.target.value === '' ? '' : Number(e.target.value))}
                style={inputStyle}
              >
                <option value="">— None —</option>
                {divisions.map((d) => (
                  <option key={d.id} value={d.id}>{d.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Class">
              <input
                type="text"
                value={classText}
                onChange={(e) => setClassText(e.target.value)}
                maxLength={100}
                placeholder="e.g. Foreman, Superintendent"
                style={inputStyle}
              />
            </Field>
          </div>

          {/* ⚠️ The join key to the travel roster (#396), and the reason it is
              typed here rather than matched on the name: a leader's go-by name
              is often not their Paycor name. Most rows were filled by the
              backfill; this is how the leftovers get resolved. */}
          <Field label="Paycor employee ID">
            <input
              type="text"
              value={employeeId}
              onChange={(e) => setEmployeeId(e.target.value)}
              maxLength={50}
              placeholder="e.g. 10652"
              style={inputStyle}
            />
            <div style={{ marginTop: 4, fontSize: 11, color: 'var(--ew-n-500)', lineHeight: 1.4 }}>
              Links this leader to the travel roster. Without it the Travel
              column stays blank, whatever their preference actually is.
            </div>
          </Field>

          <div style={{ marginTop: 4, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ew-n-500)' }}>
            Current Assignment
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 14 }}>
            <Field label="Project">
              <ProjectSelect
                ref={focusRefs.currentAssignmentId as React.RefObject<HTMLSelectElement | null>}
                value={currentAssignmentId}
                onChange={setCurrentAssignmentId}
                projects={projectOptions}
              />
            </Field>
            <Field label="Shift">
              <select
                value={assignmentShift}
                onChange={(e) => setAssignmentShift(e.target.value)}
                style={inputStyle}
              >
                <option value="">Day</option>
                <option value="Night">Night</option>
              </select>
            </Field>
            <Field label="Finish Date">
              <input
                ref={focusRefs.currentFinish as React.RefObject<HTMLInputElement | null>}
                type="date"
                value={currentFinish}
                onChange={(e) => setCurrentFinish(e.target.value)}
                style={inputStyle}
              />
            </Field>
          </div>

          <div style={{ marginTop: 4, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ew-n-500)' }}>
            Upcoming
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14 }}>
            <Field label="Next Project">
              <ProjectSelect
                ref={focusRefs.upcomingJobId as React.RefObject<HTMLSelectElement | null>}
                value={upcomingJobId}
                onChange={setUpcomingJobId}
                projects={projectOptions}
              />
            </Field>
            <Field label="Next Available">
              <input
                ref={focusRefs.nextAvailable as React.RefObject<HTMLInputElement | null>}
                type="date"
                value={nextAvailable}
                onChange={(e) => setNextAvailable(e.target.value)}
                style={inputStyle}
              />
            </Field>
          </div>

          {saveMutation.isError && (
            <div style={{ padding: '10px 14px', background: '#FFF5F5', border: '1px solid #FED7D7', borderRadius: 'var(--ew-r-md)', fontSize: 13, color: 'var(--ew-danger)' }}>
              Save failed: {(saveMutation.error as Error).message}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 24px', borderTop: '1px solid var(--ew-n-200)' }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!name.trim() || saveMutation.isPending}
          >
            {saveMutation.isPending ? 'Saving…' : isNew ? 'Add Field Leader' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─── Tiny presentational helpers ─────────────────────────────────────────────

interface BadgeProps {
  tone: 'danger' | 'warning';
  children: React.ReactNode;
  /** Override the default left margin — e.g. when the badge stands alone in a cell. */
  style?: React.CSSProperties;
}

/**
 * Small pill used to annotate cells in the field-leaders table — "Past finish",
 * "Ends in 5d", "No next assignment", "8d gap". Tone maps to the brand danger
 * (red) or performance (amber) tokens.
 */
/**
 * The Travel cell. Read-only — mp_travel_roster owns this field.
 *
 * ⚠️ The two missing-data states are LABELLED, not blank. A blank cell reads as
 * "this person does not travel", which is the opposite of "we do not know", and
 * acting on it sends the wrong crew. See travelCellState.
 */
function TravelCell({ leader }: { leader: MpLeader }) {
  const state = travelCellState(leader);

  if (state.kind !== 'known') {
    return (
      <span
        title={state.title}
        style={{
          fontSize: 11, fontStyle: 'italic', color: 'var(--ew-n-400)',
          borderBottom: '1px dotted var(--ew-n-300)', cursor: 'help',
        }}
      >
        {state.label}
      </span>
    );
  }

  const pref = leader.travel!.travelPref;
  // Categories, not a scale, so no red/amber/green ramp.
  const tone = pref === 'Traveler'
    ? { bg: 'var(--ew-quality-100)', fg: 'var(--ew-quality-700)' }
    : pref === '50/50'
      ? { bg: 'var(--ew-n-100)', fg: 'var(--ew-n-700)' }
      : { bg: 'transparent', fg: 'var(--ew-n-500)' };

  return (
    <span title={state.title}>
      <span style={{
        display: 'inline-block', padding: '1px 7px', fontSize: 10, fontWeight: 700,
        borderRadius: 'var(--ew-r-lg)', background: tone.bg, color: tone.fg,
        whiteSpace: 'nowrap', verticalAlign: 'middle',
      }}>
        {pref}
      </span>
      {leader.travel!.twic && (
        <span title="Holds a TWIC card" style={{ marginLeft: 5, fontSize: 10, color: 'var(--ew-n-500)' }}>TWIC</span>
      )}
      {/* Only shown when there IS a date. Blank means available now, and
          printing "Now" on every one of 49 rows is noise, not information. */}
      {leader.travel!.availableFrom && (
        <span
          title="Free from this date, per the travel roster"
          style={{ marginLeft: 5, fontSize: 10, color: 'var(--ew-n-500)', fontVariantNumeric: 'tabular-nums' }}
        >
          {fmtDate(leader.travel!.availableFrom)}
        </span>
      )}
    </span>
  );
}

function Badge({ tone, children, style }: BadgeProps) {
  const palette =
    tone === 'danger'
      ? { bg: 'rgba(184, 50, 28, 0.12)', fg: 'var(--ew-danger)' }
      : { bg: 'rgba(219, 122, 37, 0.14)', fg: 'var(--ew-performance)' };
  return (
    <span
      style={{
        display: 'inline-block',
        marginLeft: 6,
        padding: '1px 7px',
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.03em',
        borderRadius: 'var(--ew-r-lg)',
        background: palette.bg,
        color: palette.fg,
        whiteSpace: 'nowrap',
        verticalAlign: 'middle',
        ...style,
      }}
    >
      {children}
    </span>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '7px 10px',
  fontSize: 13,
  border: '1px solid var(--ew-n-300)',
  borderRadius: 'var(--ew-r-md)',
  fontFamily: 'var(--ew-font)',
  color: 'var(--ew-dark)',
  background: 'var(--ew-white)',
  boxSizing: 'border-box',
};

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label style={{ display: 'grid', gap: 5 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ew-n-500)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
        {label}{required && <span style={{ color: 'var(--ew-danger)' }}> *</span>}
      </span>
      {children}
    </label>
  );
}

// ─── Editable cell trigger ───────────────────────────────────────────────────

interface EditableCellProps {
  /** Read-only callers render the value with no edit affordance. */
  canEdit: boolean;
  label: string;
  onOpen: () => void;
  children: React.ReactNode;
}

/**
 * Wraps a cell so clicking it opens the edit modal focused on that field.
 * Falls back to plain content when the user lacks manpower.edit, so read-only
 * users see no affordance at all.
 */
function EditableCell({ canEdit, label, onOpen, children }: EditableCellProps) {
  if (!canEdit) return <>{children}</>;
  return (
    <button type="button" className="lv-cell-edit" onClick={onOpen} title={`Click to change ${label}`}>
      {children}
    </button>
  );
}


interface ProjectSelectProps {
  value: number | '';
  onChange: (v: number | '') => void;
  projects: ProjectLite[];
  /** Set when a row cell click should land focus on this control. */
  ref?: React.RefObject<HTMLSelectElement | null>;
}

function ProjectSelect({ value, onChange, projects, ref }: ProjectSelectProps) {
  return (
    <select
      ref={ref}
      value={value}
      onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
      style={inputStyle}
    >
      <option value="">— None —</option>
      {projects.map((p) => (
        <option key={p.id} value={p.id}>
          {p.jobNumber} · {p.title}
        </option>
      ))}
    </select>
  );
}
